import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { useFormattedZoneList, useLoading, useServerError } from 'shared/hooks'
import { notificationMessageVar } from 'modulesCommon/store'

import { useUpdateArtistMutation } from 'modulesCommon/graphql/mutations/uodateArtist.types'
import { useGpsUnits, useZonesList } from 'modulesCommon/serviceHooks'

import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'
import { artistFormSchema } from '../constants'
import { isArtistEqual } from '../utils'

import { ArtistUpdate, Artist } from 'shared/__gql-types'

const useServiceUpdateArtist = (artist: Artist) => {
  const navigate = useNavigate()
  const { zoneList, zoneError, zoneLoading } = useZonesList()
  const { zoneOptions } = useFormattedZoneList(zoneList)
  const { gpsUnitsLoading, gpsUnitsError, gpsUnits } = useGpsUnits()

  const [updateArtist, { loading: updateArtistLoading, error: updateArtistError }] = useUpdateArtistMutation()

  const showSuccessMessage = useCallback((message: string = 'Artist has been successfully updated') => {
    notificationMessageVar({
      message,
      title: 'Success',
      type: 'success'
    })
  }, [])

  const gpsUnitsOptions = useMemo(() => [{ value: '', label: '' }, ...gpsUnits], [gpsUnits])

  const defaultValues = useMemo(
    () => ({
      id: artist.id ?? '',
      label_id: artist.label_id ?? '',
      name: artist.name,
      gps_status: artist.gps_status,
      facebook_id: artist.facebook_id ?? '',
      gfk_id: artist.gfk_id,
      opus_id: artist.opus_id,
      spotify_url: artist.spotify_url ?? ''
    }),
    [artist]
  )

  const methods = useForm<ArtistUpdate>({ defaultValues, resolver: yupResolver(artistFormSchema) })
  const opusIdValue = methods.watch('opus_id')

  const onUpdateArtist = useCallback((values, isArtistChanged) => {
    delete values.products

    if (isArtistChanged) {
      updateArtist({
        variables: { input: { ...values } }
      }).then(() => {
        navigate('/admin/artists')
        showSuccessMessage()
      }).catch((e) => {
        notificationMessageVar({
          message: e?.message,
          title: 'Error',
          type: 'danger'
        })
      })
    } else {
      navigate('/admin/artists')
      showSuccessMessage()
    }
  },[navigate, updateArtist, showSuccessMessage])

  const onSubmitArtist = useCallback(
    (values: ArtistUpdate) => {
      const isDirty = methods.formState.isDirty
      onUpdateArtist(values, isDirty && !isArtistEqual(values, defaultValues))
    },
    [methods.formState.isDirty, defaultValues, onUpdateArtist]
  )

  useLoading({
    isLoading: zoneLoading || gpsUnitsLoading || updateArtistLoading
  })

  useServerError([zoneError, gpsUnitsError, updateArtistError])

  return {
    zoneOptions,
    gpsUnitsOptions,
    onSubmitArtist,
    methods,
    opusIdValue,
  }
}

export default useServiceUpdateArtist
